import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { QueryService } from '../../comun/services/query.service';
import { GlobalConstants } from '../../global-constants';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UsuarioPerfil } from '../../comun/models/misdatos.model';
import { PerfilService } from '../../comun/services/perfil.service';

@Component({
  selector: 'app-visitas-modal',
  templateUrl: './visitas-modal.component.html',
  styleUrls: ['./visitas-modal.component.css']
})
export class VisitasModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scrollContainerVisitas') scrollContainer?: ElementRef<HTMLDivElement>;
  global = GlobalConstants;
  perfil: any = {};
  visitas: any[] = [];
  load = false;
  cargandoMas = false;
  noHayMas = false;
  error = '';

  private perfilSub?: Subscription;
  private visitasSub?: Subscription;
  private numeroSub?: Subscription;
  private modal?: HTMLElement;
  private activo = false;
  private cargadas = new Set<string>();
  private readonly mostrar = () => this.abrir();
  private readonly ocultar = () => this.cerrar();

  constructor(
    private query: QueryService,
    private perfilService: PerfilService,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.perfilSub = this.perfilService.perfil$
      .pipe(filter((perfil): perfil is UsuarioPerfil => perfil !== null))
      .subscribe(perfil => { this.perfil = perfil; this.cdr.markForCheck(); });
    this.modal = document.getElementById('visitasModal') || undefined;
    this.modal?.addEventListener('shown.bs.modal', this.mostrar);
    this.modal?.addEventListener('hidden.bs.modal', this.ocultar);
    // La carga inicial se hace al abrir, no al instanciar el custom element.
  }

  private abrir(): void {
    this.activo = true;
    this.visitas = [];
    this.cargadas.clear();
    this.noHayMas = false;
    this.error = '';
    if (this.scrollContainer) this.scrollContainer.nativeElement.scrollTop = 0;
    this.getVisitas();
  }

  private cerrar(): void {
    this.activo = false;
    this.visitasSub?.unsubscribe();
    this.numeroVisitas();
  }

  getVisitas(): void {
    if (this.load || this.cargandoMas) return;
    this.load = true;
    this.error = '';
    this.visitasSub?.unsubscribe();
    this.visitasSub = this.query.query('visita', 'GET', null).subscribe({
      next: (resultado: any) => {
        if (!this.activo) return;
        if (resultado?.resultado === 'ERROR') {
          this.error = resultado.mensaje || 'No se pudieron cargar las visitas.';
        } else {
          this.agregarVisitas(resultado?.registros);
        }
        this.load = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (!this.activo) return;
        this.error = 'No se pudieron cargar las visitas. Inténtalo de nuevo.';
        this.load = false;
        this.cdr.markForCheck();
      }
    });
  }

  private agregarVisitas(registros: any): number {
    const lote = Array.isArray(registros) ? registros : [];
    let nuevas = 0;
    for (const visita of lote) {
      const clave = String(visita?.vi_id ?? `${visita?.slug || ''}:${visita?.vi_fechahora_bd || visita?.vi_fechahora || ''}`);
      if (!this.cargadas.has(clave)) {
        this.cargadas.add(clave);
        this.visitas.push(visita);
        nuevas++;
      }
    }
    // Sin más páginas si el servidor no devuelve registros; también evita
    // bucles si una página repite exclusivamente las visitas anteriores.
    if (!lote.length || !nuevas) this.noHayMas = true;
    return nuevas;
  }

  numeroVisitas(): void {
    const id = Number(this.perfil?.ID || 0);
    if (!id) return;
    this.numeroSub?.unsubscribe();
    this.numeroSub = this.query.query('visita', 'NUMERO', { ID: id }).subscribe({
      next: (resultado: any) => {
        if (!this.perfil?.datos || resultado?.resultado === 'ERROR') return;
        this.perfil.datos.visitas_total = resultado.total;
        this.perfil.datos.visitas_nuevas = resultado.nuevas;
        this.cdr.markForCheck();
      },
      error: () => { /* El contador no debe impedir cerrar el modal. */ }
    });
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el || this.load || this.cargandoMas || this.noHayMas || !this.visitas.length) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 170) this.cargarVisitasSiguientes();
  }

  cargarVisitasSiguientes(): void {
    if (!this.activo || this.load || this.cargandoMas || this.noHayMas || !this.visitas.length) return;
    const ultima = this.visitas[this.visitas.length - 1];
    const cursor = ultima?.vi_fechahora_bd;
    if (!cursor) { this.noHayMas = true; return; }
    this.cargandoMas = true;
    this.visitasSub?.unsubscribe();
    this.visitasSub = this.query.query('visita', 'GET', { fecha_siguiente: cursor }).subscribe({
      next: (resultado: any) => {
        if (!this.activo) return;
        if (resultado?.resultado === 'ERROR') {
          this.error = resultado.mensaje || 'No se pudieron cargar más visitas.';
        } else {
          this.agregarVisitas(resultado?.registros);
        }
        this.cargandoMas = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (!this.activo) return;
        this.error = 'No se pudieron cargar más visitas.';
        this.cargandoMas = false;
        this.cdr.markForCheck();
      }
    });
  }

  goPerfil(visita: any): void {
    const slug = String(visita?.slug ?? '').trim();
    if (!slug) return;
    window.open(this.global.urlServer.replace(/\/?$/, '/') + 'perfil/' + encodeURIComponent(slug), '_blank', 'noopener,noreferrer');
  }

  trackByVisita(index: number, visita: any): string {
    return String(visita?.vi_id ?? `${visita?.slug}:${visita?.vi_fechahora_bd ?? index}`);
  }

  getNombre(visita: any): string {
    return [visita?.mascota?.ma_nombre, visita?.first_name].filter(Boolean).join(' y ') || 'Miembro de iGuau';
  }

  ngOnDestroy(): void {
    this.activo = false;
    this.modal?.removeEventListener('shown.bs.modal', this.mostrar);
    this.modal?.removeEventListener('hidden.bs.modal', this.ocultar);
    this.perfilSub?.unsubscribe();
    this.visitasSub?.unsubscribe();
    this.numeroSub?.unsubscribe();
  }
}
